import { getCookie } from 'cookies-next';

/** Joins URL segments into a single URL */
export const urlJoin = (...args: string[]): string => {
	return args.map((s) => s.replace(/\/+$|^\/+/g, '')).join('/');
};

export const toCamelCaseObj = (pascalCaseObj: any): any => {
	if (pascalCaseObj === null || pascalCaseObj === undefined) {
		return pascalCaseObj;
	}

	if (Array.isArray(pascalCaseObj)) {
		return toCamelCaseArray(pascalCaseObj);
	}

	const camelCaseObj: any = {};

	Object.keys(pascalCaseObj).forEach((key) => {
		const camelCaseKey = key[0].toLowerCase() + key.slice(1);
		camelCaseObj[camelCaseKey] =
			typeof pascalCaseObj[key] === 'object'
				? toCamelCaseObj(pascalCaseObj[key])
				: pascalCaseObj[key];
	});

	return camelCaseObj;
};

const toCamelCaseArray = (pascalCaseArray: any[]): any[] =>
	pascalCaseArray.map((item) =>
		typeof item === 'object' ? toCamelCaseObj(item) : item
	);

export const slugify = (text: string) => {
	return String(text ?? '')
		.toLowerCase()
		.replace(/\s+/g, '-') // Replace spaces with -
		.replace(/[^\w-]+/g, '') // Remove all non-word chars
		.replace(/--+/g, '-'); // Replace multiple - with single -
};

/** Returns grouped array, sorted ascending by key */
export const groupBy = <T>(
	s: T[],
	keyFn: (t: T) => string
): Map<string, T[]> => {
	const map = new Map<string, T[]>();
	s.forEach((t) => {
		const key = keyFn(t);
		if (!map.has(key)) {
			map.set(key, new Array<T>());
		}
		map.get(key)?.push(t);
	});
	for (const key of map.keys()) {
		map.get(key)?.sort((a, b) => keyFn(a).localeCompare(keyFn(b)));
	}
	map.forEach((value) =>
		value.sort((a, b) => keyFn(a).localeCompare(keyFn(b)))
	);
	return map;
};

/**
 * Extracts the YouTube video ID from a given URL. Supports multiple YouTube URL formats.
 * @param url - The YouTube URL to parse.
 * @returns The video ID if found, otherwise null.
 */
export const parseIdFromYoutubeUrl = (url: string): string | null => {
	const regex =
		/(?:embed\/|v=|vi\/|youtu\.be\/|\/v\/|\/e\/|watch\?v=)([\w\-_]{11})/;
	const match = url.match(regex);
	return match ? match[1] : null;
};

export const checkCookieYesConsent = (consentType: string): boolean => {
	const consentCookie = getCookie('cookieyes-consent') as string;

	if (!consentCookie) {
		return false;
	}
	const cyConsents = parseKeyValueString(consentCookie);
	return cyConsents.find((c) => c.key === consentType)?.value === 'yes';
};

/**
 * Converts a comma-separated key:value string into a searchable key-value array.
 * @param input The input string to parse.
 * @returns Array of objects with key and value properties.
 */
function parseKeyValueString(input: string): { key: string; value: string }[] {
	return input.split(',').map((pair) => {
		const [key, value = ''] = pair.split(':');
		return { key, value };
	});
}

export const nonNull = <T>(array: (T | undefined)[] | undefined): T[] => {
	return array?.filter((item): item is T => item !== undefined) ?? [];
};
